import React, { useMemo } from 'react';
import { Check, Copy, FileWarning, Info, Loader2, RefreshCw, Search } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const shortId = (id) => (id ? `...${String(id).slice(-8)}` : '-');

const formatDateTime = (dateString) => {
  if (!dateString) return 'Unknown';
  let normalizedDate = dateString;
  if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
    normalizedDate = `${dateString}Z`;
  }
  return new Date(normalizedDate).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const selectClassName = 'h-9 rounded-md border border-gray-700 bg-gray-950 px-3 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-amber-500';

const StatPill = ({ label, value }) => (
  <Badge variant="outline" className="border-gray-700 text-gray-300">
    {label}: {value}
  </Badge>
);

const getFailureType = (failure) => failure.category || failure.error_type || failure.route || 'unknown';

export const AdminGenerationFailures = ({
  failures = [],
  stats = null,
  total = 0,
  loading = false,
  statsLoading = false,
  showControls = true,
  filters = {},
  onFiltersChange,
  onRefresh,
  onViewCaseInfo,
  onCopyId,
  copiedId,
}) => {
  const categoryOptions = useMemo(() => {
    const fromStats = (stats?.by_category || [])
      .map((row) => row.category)
      .filter(Boolean);
    const fromRows = failures.map(getFailureType).filter(Boolean);
    return Array.from(new Set([...fromStats, ...fromRows]));
  }, [failures, stats]);

  const updateFilter = (key, value) => {
    onFiltersChange?.({
      ...filters,
      [key]: value,
      offset: 0,
    });
  };

  const clearFilters = () => {
    onFiltersChange?.({
      category: '',
      retryable: '',
      theme: '',
      case_id: '',
      limit: filters.limit || 100,
      offset: 0,
    });
  };

  const hasFilters = Boolean(filters.category || filters.retryable || filters.theme || filters.case_id);
  const showRowActions = Boolean(onViewCaseInfo || onCopyId);
  const columnCount = showRowActions ? 7 : 6;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Generation Failures</h2>
          <p className="text-sm text-gray-400">
            {loading ? 'Loading failures...' : `Showing ${failures.length} of ${total || failures.length} stored diagnostics`}
          </p>
        </div>
        {showControls && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading || statsLoading || !onRefresh}
            className="w-fit border-gray-700 text-gray-200"
          >
            <RefreshCw className={`h-4 w-4 ${loading || statsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {statsLoading ? (
          <Badge variant="outline" className="border-gray-700 text-gray-400">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Loading stats
          </Badge>
        ) : (
          <>
            {(stats?.by_category || []).slice(0, 6).map((row) => (
              <StatPill key={`category-${row.category}`} label={row.category || 'unknown'} value={row.count || 0} />
            ))}
            {(stats?.by_theme || []).slice(0, 4).map((row) => (
              <StatPill key={`theme-${row.theme}`} label={row.theme || 'unknown'} value={row.count || 0} />
            ))}
            {(stats?.by_retryable || []).map((row) => (
              <StatPill key={`retryable-${String(row.retryable)}`} label={`retryable ${String(row.retryable)}`} value={row.count || 0} />
            ))}
          </>
        )}
      </div>

      {showControls && (
        <Card className="rounded-lg border-gray-700 bg-gray-900/60 shadow-none">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-[180px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Category
                <select className={selectClassName} value={filters.category || ''} onChange={(event) => updateFilter('category', event.target.value)}>
                  <option value="">All</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
              <label className="flex min-w-[150px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Retryable
                <select className={selectClassName} value={filters.retryable || ''} onChange={(event) => updateFilter('retryable', event.target.value)}>
                  <option value="">Any</option>
                  <option value="true">Retryable</option>
                  <option value="false">Not retryable</option>
                </select>
              </label>
              <label className="flex min-w-[150px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Theme
                <select className={selectClassName} value={filters.theme || ''} onChange={(event) => updateFilter('theme', event.target.value)}>
                  <option value="">All</option>
                  <option value="noir">1940s Noir</option>
                  <option value="90s_nyc">90s NYC</option>
                  <option value="miami_vice">80s Miami</option>
                </select>
              </label>
              <label className="flex min-w-[220px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Case ID
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-600" />
                  <Input
                    value={filters.case_id || ''}
                    onChange={(event) => updateFilter('case_id', event.target.value)}
                    placeholder="case id"
                    className="border-gray-700 bg-gray-950 pl-8 text-gray-200 placeholder:text-gray-600"
                  />
                </div>
              </label>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white">
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px]">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/60">
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Failure</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Case</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Route</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Retry</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Theme</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Created</th>
                {showRowActions && (
                  <th className="p-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="p-8 text-center text-gray-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading generation failures
                  </td>
                </tr>
              ) : failures.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="p-8 text-center text-gray-500">
                    No generation failures match the current filters.
                  </td>
                </tr>
              ) : failures.map((failure, index) => {
                const caseId = failure.case_id;
                const failureId = failure.id || failure.failure_id || `${caseId || 'failure'}-${index}`;
                const retryable = failure.retryable === true;
                const resolved = failure.resolved === true;

                return (
                  <tr key={failureId} className="hover:bg-gray-800/40 transition-colors">
                    <td className="p-3 align-top">
                      <div className="flex items-center gap-2">
                        <FileWarning className="h-4 w-4 text-red-400" />
                        <span className="font-medium text-white">{getFailureType(failure)}</span>
                      </div>
                      <p className="mt-1 max-w-[420px] text-sm text-gray-400 line-clamp-2">
                        {failure.error_message || failure.cause_message || failure.message || 'No message stored'}
                      </p>
                    </td>
                    <td className="p-3 align-top">
                      {caseId && onCopyId ? (
                        <button
                          type="button"
                          onClick={() => onCopyId(caseId, 'case')}
                          className="flex items-center gap-1 font-mono text-xs text-gray-400 hover:text-gray-200"
                          title={caseId}
                        >
                          {shortId(caseId)}
                          {copiedId === caseId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        </button>
                      ) : (
                        <span className="font-mono text-xs text-gray-500" title={caseId || ''}>
                          {shortId(caseId)}
                        </span>
                      )}
                    </td>
                    <td className="p-3 align-top text-sm text-gray-300">
                      {failure.route || '-'}
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        <Badge className={retryable
                          ? 'bg-amber-900/50 text-amber-300 border border-amber-700'
                          : 'bg-gray-800 text-gray-400 border border-gray-700'}
                        >
                          {retryable ? 'retryable' : 'not retryable'}
                        </Badge>
                        <Badge className={resolved
                          ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'
                          : 'bg-red-900/40 text-red-300 border border-red-800'}
                        >
                          {resolved ? 'resolved' : 'open'}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3 align-top text-sm capitalize text-gray-300">
                      {failure.theme?.replace(/_/g, ' ') || '-'}
                    </td>
                    <td className="p-3 align-top text-sm text-gray-400">
                      {formatDateTime(failure.created_at || failure.timestamp)}
                    </td>
                    {showRowActions && (
                      <td className="p-3 align-top">
                        <div className="flex justify-end gap-1">
                          {caseId && onViewCaseInfo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onViewCaseInfo(caseId, failure.session_id)}
                              className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                              title="View case info"
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          )}
                          {caseId && onCopyId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onCopyId(caseId, 'case')}
                              className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                              title="Copy case ID"
                            >
                              {copiedId === caseId ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminGenerationFailures;
